import { ArrowRight, Search, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";

const HERO_IMG =
  "https://images.unsplash.com/photo-1615766553246-9147b6d50e90?w=1100&q=80&auto=format&fit=crop";

const Hero = () => {
  const reduce = useReducedMotion();
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-br from-white via-white to-maternnia-porcelana"
    >
      {/* Organic background shapes */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="animate-float-slow absolute -top-24 -right-16 w-[34rem] h-[34rem] rounded-full bg-maternnia-nevoa/50 blur-3xl" />
        <div className="animate-float-slow2 absolute top-1/3 -left-24 w-[26rem] h-[26rem] rounded-full bg-maternnia-pink/10 blur-3xl" />
        <div className="animate-float-slow absolute -bottom-32 right-1/4 w-[22rem] h-[22rem] rounded-full bg-maternnia-eucalipto/[0.07] blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 lg:py-36 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          {/* Text */}
          <motion.div
            className="lg:col-span-6"
            initial={reduce ? false : { opacity: 0, y: 30 }}
            animate={reduce ? {} : { opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
          >
            <span className="inline-flex items-center gap-2 bg-white/70 border border-maternnia-nevoa text-maternnia-eucalipto text-xs font-medium tracking-wide px-4 py-2 rounded-full mb-8">
              <Sparkles size={14} /> Do planejamento ao pós-parto
            </span>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.04] tracking-tight text-maternnia-charcoal font-medium">
              Sua jornada, com mais clareza, confiança e apoio.
            </h1>
            <p className="mt-7 text-base md:text-lg text-maternnia-taupe leading-relaxed max-w-xl font-light">
              A Maternnia reúne orientação confiável, profissionais cuidadosamente selecionados e recursos para ajudar você a fazer escolhas mais conscientes em cada fase da maternidade.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
              <a
                href="#etapas"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-2 bg-maternnia-pink text-white hover:bg-maternnia-pinkHover rounded-full px-8 py-4 text-base font-medium transition-all duration-300 shadow-sm hover:shadow-lg hover:scale-[1.03] active:scale-[0.98]"
              >
                Explorar minha etapa
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link
                to="/profissionais"
                data-testid="hero-secondary-cta"
                className="group inline-flex items-center justify-center gap-2 border border-maternnia-charcoal/20 text-maternnia-charcoal hover:bg-maternnia-charcoal hover:text-white rounded-full px-8 py-4 text-base font-medium transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
              >
                <Search size={17} className="transition-transform duration-300 group-hover:scale-110" />
                Encontrar profissionais
              </Link>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-5">
              <Link
                to="/fornecedores"
                data-testid="hero-fornecedor-link"
                className="text-sm font-medium text-maternnia-eucalipto hover:text-maternnia-floresta underline-offset-4 hover:underline transition-colors"
              >
                Sou fornecedor
              </Link>
              <span className="text-xs tracking-wide text-maternnia-taupe/80">
                Conteúdo gratuito • Curadoria • São Paulo
              </span>
            </div>
          </motion.div>

          {/* Image — asymmetric */}
          <motion.div
            className="lg:col-span-6 lg:pl-8"
            initial={reduce ? false : { opacity: 0 }}
            animate={reduce ? {} : { opacity: 1 }}
            transition={{ duration: 1, ease }}
          >
            <div className="relative lg:translate-y-6 lg:ml-auto lg:max-w-[34rem]">
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_30px_70px_rgba(43,37,38,0.14)] aspect-[4/5]">
                <motion.img
                  src={HERO_IMG}
                  alt="Gestante acariciando a barriga com as mãos em formato de coração, vestido rosa"
                  className="w-full h-full object-cover"
                  initial={reduce ? false : { scale: 1.12 }}
                  animate={reduce ? {} : { scale: 1 }}
                  transition={{ duration: 1.8, ease }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-maternnia-charcoal/10 to-transparent" />
              </div>
              <div className="absolute -top-5 -left-5 w-24 h-24 rounded-3xl border border-maternnia-pink/30 hidden md:block" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
